/**
 * useAgentActivity Hook
 * AIDLC Administrative Backend
 *
 * Provides live agent activity feed data:
 *   - Fetches initial data via REST (GET /api/agents/:id/activity)
 *   - Subscribes to 'agent:output' WebSocket events for live streaming
 *   - Subscribes to 'agent:status' for active task changes
 *   - Deduplicates by lineNumber, ring buffers client-side (200 lines)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getAgentActivity } from '../services/agentService';
import type { ActivityLine, ActiveTask } from '../services/agentService';
import { useBridge } from '../components/layout/MainLayout';

const RING_BUFFER_SIZE = 200;

interface UseAgentActivityResult {
  activeTask: ActiveTask | null;
  lines: ActivityLine[];
  isLoading: boolean;
  error: string | null;
}

export function useAgentActivity(agentId: string): UseAgentActivityResult {
  const [activeTask, setActiveTask] = useState<ActiveTask | null>(null);
  const [lines, setLines] = useState<ActivityLine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { socket } = useBridge();
  const maxLineNumberRef = useRef(-1);

  // Fetch initial data
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getAgentActivity(agentId);
        if (!cancelled) {
          setActiveTask(data.activeTask);
          setLines(data.lines);
          // Track max lineNumber for dedup
          if (data.lines.length > 0) {
            maxLineNumberRef.current = Math.max(
              ...data.lines.map((l) => l.lineNumber)
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load activity');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [agentId]);

  // Subscribe to agent:output WebSocket events
  const handleOutput = useCallback(
    (...args: unknown[]) => {
      const data = args[0] as {
        agentId: string;
        taskId: string;
        line: string;
        lineType: 'stdout' | 'stderr' | 'system';
        lineNumber: number;
        timestamp: string;
      };
      if (!data || data.agentId !== agentId) return;

      // Deduplicate by lineNumber
      if (data.lineNumber <= maxLineNumberRef.current) return;
      maxLineNumberRef.current = data.lineNumber;

      const newLine: ActivityLine = {
        lineNumber: data.lineNumber,
        line: data.line,
        lineType: data.lineType,
        timestamp: data.timestamp,
      };

      setLines((prev) => {
        const updated = [...prev, newLine];
        // Ring buffer
        if (updated.length > RING_BUFFER_SIZE) {
          return updated.slice(updated.length - RING_BUFFER_SIZE);
        }
        return updated;
      });
    },
    [agentId]
  );

  // Subscribe to agent:status WebSocket events for active task changes
  const handleStatus = useCallback(
    (...args: unknown[]) => {
      const data = args[0] as {
        agentId: string;
        activeTask: ActiveTask | null;
      };
      if (!data || data.agentId !== agentId) return;
      setActiveTask(data.activeTask || null);
    },
    [agentId]
  );

  useEffect(() => {
    if (!socket) return;

    socket.on('agent:output', handleOutput);
    socket.on('agent:status', handleStatus);

    // Subscribe to agent-specific room
    socket.emit('subscribe:agent', { agentId });

    return () => {
      socket.off('agent:output', handleOutput);
      socket.off('agent:status', handleStatus);
      socket.emit('unsubscribe:agent', { agentId });
    };
  }, [socket, agentId, handleOutput, handleStatus]);

  return { activeTask, lines, isLoading, error };
}
